import { ctaInteraction, ga4Track, tooltipInteraction } from '@unisporkal/ga4';

const UI_ELEMENT_LOCATION = {
  ADP_BUY_CARD: 'adp_buy_card',
  ADP_BUY_CARD_CREATIVE_SUBS: 'adp_buy_card_creative_subs',
  ADP_BUY_CARD_EDITORIAL_SUBS: 'adp_buy_card_editorial_subs',
  ADP_BUY_CARD_CREATIVE_ALC: 'adp_buy_card_creative_alc',
  ADP_BUY_CARD_EDITORIAL_ALC: 'adp_buy_card_editorial_alc',
  ADP_BUY_CARD_CREATIVE_UP: 'adp_buy_card_creative_up',
  ADP_BUY_CARD_EDITORIAL_UP: 'adp_buy_card_editorial_up',
  ADP_BUY_CARD_EDITORIAL: 'adp_buy_card_editorial',
  REFINE_PANEL: 'refine_panel',
} as const;

export const trackClickCreateLicense = (): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: 'create_license',
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.ADP_BUY_CARD,
  });
  ga4Track(ga4Event);
};

export const trackClickClientLicenses = (): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: 'client_licenses',
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.ADP_BUY_CARD,
  });
  ga4Track(ga4Event);
};

export const trackClickViewLicenseDetailsCreativeSubs = (
  ctaText: string
): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: ctaText,
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.ADP_BUY_CARD_CREATIVE_SUBS,
  });
  ga4Track(ga4Event);
};

export const trackClickViewLicenseDetailsEditorialSubs = (
  ctaText: string
): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: ctaText,
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.ADP_BUY_CARD_EDITORIAL_SUBS,
  });
  ga4Track(ga4Event);
};

export const trackClickViewLicenseDetailsCreativeAlc = (
  ctaText: string
): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: ctaText,
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.ADP_BUY_CARD_CREATIVE_ALC,
  });
  ga4Track(ga4Event);
};

export const trackClickViewLicenseDetailsEditorialAlc = (
  ctaText: string
): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: ctaText,
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.ADP_BUY_CARD_EDITORIAL_ALC,
  });
  ga4Track(ga4Event);
};

export const trackClickViewLicenseDetailsCreativeUp = (
  ctaText: string
): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: ctaText,
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.ADP_BUY_CARD_CREATIVE_UP,
  });
  ga4Track(ga4Event);
};

export const trackClickViewLicenseDetailsEditorialUp = (
  ctaText: string
): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: ctaText,
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.ADP_BUY_CARD_EDITORIAL_UP,
  });
  ga4Track(ga4Event);
};

export const trackClickExploreIncludedImagesEditorial = (
  ctaUrl: string
): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: 'explore_included_images',
    cta_selection: undefined,
    cta_url: ctaUrl,
    ui_element_location: UI_ELEMENT_LOCATION.ADP_BUY_CARD_EDITORIAL,
  });
  ga4Track(ga4Event);
};

export const trackClickDownloadOptions = (): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: 'download_options',
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.REFINE_PANEL,
  });
  ga4Track(ga4Event);
};

// Maps backend modified-image size names (and legacy aliases) to the GA4
// cta_text values defined on slide 14. Grouping mirrors modifiedSizeCopy.ts:
// low|1k -> standard, medium|2k -> enhanced, high|super|4k -> higher.
const DOWNLOAD_SIZE_CTA_TEXT: Record<string, string> = {
  low: 'standard_image',
  '1k': 'standard_image',
  medium: 'enhanced_detail',
  '2k': 'enhanced_detail',
  high: 'higher_resolution',
  super: 'higher_resolution',
  '4k': 'higher_resolution',
};

export const trackRefinePanelDownloadSizeClick = (sizeName: string): void => {
  const ctaText = DOWNLOAD_SIZE_CTA_TEXT[sizeName];
  if (!ctaText) return;
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: ctaText,
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.REFINE_PANEL,
  });
  ga4Track(ga4Event);
};

export const trackRefinePanelDownloadClick = (): void => {
  const ga4Event = ctaInteraction({
    cta_interaction: 'cta_clicked',
    cta_text: 'download',
    cta_selection: undefined,
    ui_element_location: UI_ELEMENT_LOCATION.REFINE_PANEL,
  });
  ga4Track(ga4Event);
};

// --- download_interaction event family (Getty Mods download flow) ---
// download_selection mirrors the iStock content-gen value for the Mods preshot
// flow. These are hand-rolled (not the @unisporkal/ga4 downloadInteraction
// helper) because that helper does not emit asset_sizes.
const DOWNLOAD_SELECTION = 'ai_gen_modified_preshot';

export const trackInitiateDownload = ({
  assetSize,
  assetSizes,
  downloadFlow,
}: {
  assetSize: string;
  assetSizes: string[];
  downloadFlow?: string | null;
}): void => {
  ga4Track({
    event: 'download_interaction',
    event_name: 'initiate_download',
    download_interaction: 'initiate_download',
    download_selection: DOWNLOAD_SELECTION,
    download_flow: downloadFlow || undefined,
    ui_element_location: UI_ELEMENT_LOCATION.REFINE_PANEL,
    asset_size: assetSize,
    ...(assetSizes.length && { asset_sizes: assetSizes }),
  });
};

// BEST-GUESS trigger + params — confirm with Zac / GA4 team (see MR note).
// There is no spec slide for paid_download. Fired on a charge-incurring
// download (not a re-download / free comp).
export const trackPaidDownload = ({
  assetSize,
  assetSizes,
  downloadMethod,
  downloadFlow,
}: {
  assetSize: string;
  assetSizes: string[];
  downloadMethod: string;
  downloadFlow?: string | null;
}): void => {
  ga4Track({
    event: 'download_interaction',
    event_name: 'paid_download',
    download_interaction: 'paid_download',
    download_selection: DOWNLOAD_SELECTION,
    download_method: downloadMethod,
    download_flow: downloadFlow || undefined,
    ui_element_location: UI_ELEMENT_LOCATION.REFINE_PANEL,
    asset_size: assetSize,
    ...(assetSizes.length && { asset_sizes: assetSizes }),
  });
};

export const trackIncludedSizesTooltipShown = (): void => {
  ga4Track(
    tooltipInteraction({
      tooltip_interaction: 'tooltip_shown',
      tooltip_name: 'include_all_available_sizes_tooltip',
    })
  );
};
